import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Star } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DEFAULT_REVIEWS_SEGMENT,
  parseReviewsSegment,
} from "@/components/pages/analytics/front-desk/components/reviews/reviewsSegment";
import { RevenueChartLegend } from "@/components/pages/analytics/front-desk/components/revenue/RevenueChartLegend";
import { ReviewsKpiCards } from "@/components/pages/analytics/front-desk/components/reviews/ReviewsKpiCards";
import { ReviewsSegmentPills } from "@/components/pages/analytics/front-desk/components/reviews/ReviewsSegmentPills";
import type { ReviewsSegment } from "@/components/pages/analytics/front-desk/components/reviews/types";
import { useAnalyticsPage } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { useAdminReviewsHotels } from "../../hooks/useAdminReviewsHotels";
import { useAdminTenantTableExport } from "../../hooks/useAdminTenantTableExport";
import type { AdminReviewsTrendPoint } from "../../utils/adminReviewsMappers";
import { AdminReviewsHotelsTable } from "./AdminReviewsHotelsTable";

function OverallRatingTrendCard({
  data,
  loading,
}: {
  data: AdminReviewsTrendPoint[];
  loading: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const overallRatingLabel = t(
    "analytics.reviews.kpi.overallRating",
    "Overall Rating",
  );
  const legendItems = useMemo(
    () => [{ key: "overall", label: overallRatingLabel, color: "#826BFF" }],
    [overallRatingLabel],
  );

  if (loading || data.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-[#E5E5E5] bg-white p-4">
      <div className="mb-4 flex items-center gap-2">
        <Star className="size-4 text-[#CAC19F]" />
        <h3 className="text-sm font-semibold text-[#171717]">
          {t("analytics.reviews.overallTrendTitle", "Overall rating over time")}
        </h3>
      </div>
      <div className="flex flex-col gap-3">
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tickLine={false} axisLine={false} />
              <YAxis
                tickLine={false}
                axisLine={false}
                domain={[1, 5]}
                tickCount={5}
              />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="overall"
                stroke="#826BFF"
                strokeWidth={2}
                dot={false}
                name={overallRatingLabel}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <RevenueChartLegend items={legendItems} />
      </div>
    </div>
  );
}

export const AdminReviewsTab = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewsSegment = parseReviewsSegment(
    searchParams.get("reviewsSegment"),
  );
  const {
    tenantId,
    summary,
    kpiDeltas,
    tableRows,
    trendPoints,
    isLoading,
    isError,
    isReady,
  } = useAdminReviewsHotels(dateRange, reviewsSegment);

  useAdminTenantTableExport({
    tab: "reviews",
    dateRange,
    canExport: Boolean(tenantId) && isReady && !isLoading && !isError,
  });

  if (!tenantId) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.noTenant", "Tenant is not selected.")}
      </p>
    );
  }

  if (!isReady) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.reviews.selectDateRange",
          "Select a date range to view hotel reviews.",
        )}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.reviews.loadError", "Failed to load reviews data.")}
      </p>
    );
  }

  return (
    <section className="space-y-6">
      <ReviewsSegmentPills
        value={reviewsSegment}
        onChange={(value: ReviewsSegment) => {
          setSearchParams((previousParams) => {
            const nextParams = new URLSearchParams(previousParams);
            nextParams.set("tab", "reviews");
            if (value === DEFAULT_REVIEWS_SEGMENT) {
              nextParams.delete("reviewsSegment");
            } else {
              nextParams.set("reviewsSegment", value);
            }
            return nextParams;
          });
        }}
      />

      <ReviewsKpiCards summary={summary} kpiDeltas={kpiDeltas} />

      <AdminReviewsHotelsTable rows={tableRows} loading={isLoading} />

      <OverallRatingTrendCard data={trendPoints} loading={isLoading} />
    </section>
  );
};
